import { styled, useTheme } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import JobTimeline from '~components/Job/JobTimeline';
import LoadPricingPanel from '~components/Job/LoadPricing/LoadPricingPanel';
import Loads from '~components/Job/Loads';
import { ROUTE_MAP_HEIGHT_IN_PX, RouteMap } from '~components/Job/RouteMap';
import { SmallTabs } from '~components/Tabs/SmallTabs';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { Job } from '~hooks/useJob/models';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

interface Props {
  details: Job;
  readOnly: boolean;
  reload: () => Promise<void>;
}

const LoadDetails = ({ details, reload, readOnly }: Props) => {
  const theme = useTheme();
  const loadLevelPricingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.loadLevelPricing,
  });

  const loadsTableNodeRef = useRef<HTMLDivElement>();
  const [timelineContainerHeight, setTimelineContainerHeight] =
    useState(ROUTE_MAP_HEIGHT_IN_PX);

  useEffect(() => {
    if (!loadsTableNodeRef.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const [loadsContainer] = entries;

      if (loadsContainer) {
        const height = loadsContainer.borderBoxSize[0].blockSize;

        if (height > ROUTE_MAP_HEIGHT_IN_PX) {
          setTimelineContainerHeight(height);
        }
      }
    });

    observer.observe(loadsTableNodeRef.current);

    return () => {
      observer.disconnect();
    };
  }, [loadsTableNodeRef.current]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '12fr 7fr 8fr',
        gap: 2.5,
      }}
    >
      <Box>
        <Loads
          loadsTableNodeRef={loadsTableNodeRef}
          details={details}
          reload={reload}
          readOnly={readOnly}
        />
      </Box>

      <Container>
        <Title>{t('dispatch.timeline.timeline')}</Title>

        <Box
          sx={{
            flex: 1,
            minHeight: `${ROUTE_MAP_HEIGHT_IN_PX}px`,
            maxHeight: `${timelineContainerHeight}px`,
          }}
        >
          {loadLevelPricingEnabled ? (
            <SmallTabs
              panelHeight={timelineContainerHeight - 60}
              tabs={[
                {
                  label: `${t('dispatch.timeline.timeline')}`,
                  id: `details_${details.id}_unified_timeline`,
                  content: (
                    <JobTimeline
                      job={details}
                      sx={{ height: '100%', overflow: 'auto' }}
                    />
                  ),
                },
                {
                  label: `${t('loads.pricing.title')}`,
                  id: `details_${details.id}_unified_pricing`,
                  content: (
                    <LoadPricingPanel
                      jobId={details.id}
                      rateEditable={details.rateEditable}
                    />
                  ),
                },
              ]}
            />
          ) : (
            <JobTimeline
              job={details}
              sx={{
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: '4px',
                height: '100%',
                overflow: 'auto',
                pl: 1.5,
              }}
            />
          )}
        </Box>
      </Container>

      <Container>
        <Title>{t('dispatch.timeline.map_card_title')}</Title>

        <Box
          sx={{
            flex: 1,
            minHeight: `${ROUTE_MAP_HEIGHT_IN_PX}px`,
            maxHeight: `${timelineContainerHeight}px`,
          }}
        >
          <RouteMap
            job={details}
            sx={{
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderRadius: '4px',
              height: '100%',
              overflow: 'hidden',
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};

const Container = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: ROUTE_MAP_HEIGHT_IN_PX,
}));

const Title = styled(Typography)<TypographyProps>(({ theme }) => ({
  color: theme.brandV2.colors.treadBlack,
  fontSize: '14px',
  fontWeight: 600,
  minHeight: `calc(${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px + ${theme.spacing(0.5)})`,
}));

export { LoadDetails };
